const SCROLL_RELEASE_DURATION_MS = 600;
const DEFAULT_SESSION_COOKIE_NAME = 'kronos_home_loader_seen';

function markHomeLoaderSeen(loader) {
    const cookieName = loader.dataset.sessionCookie || DEFAULT_SESSION_COOKIE_NAME;

    document.cookie = `${encodeURIComponent(cookieName)}=1; path=/; SameSite=Lax`;

    try {
        sessionStorage.setItem(cookieName, '1');
    } catch {
        // sessionStorage may be unavailable in private mode or strict settings.
    }
}

function measureScrollbarWidth() {
    const existing = window.innerWidth - document.documentElement.clientWidth;

    if (existing > 0) {
        return existing;
    }

    const parent = document.createElement('div');
    parent.style.cssText =
        'position:absolute;visibility:hidden;overflow:scroll;width:100px;height:100px;top:-9999px';
    document.body.appendChild(parent);

    const child = document.createElement('div');
    child.style.width = '100%';
    child.style.height = '200px';
    parent.appendChild(child);

    const width = parent.offsetWidth - child.offsetWidth;
    parent.remove();

    return width;
}

function applyScrollLockCompensation() {
    const width = measureScrollbarWidth();

    if (width > 0) {
        document.body.style.setProperty('--kronos-scrollbar-width', `${width}px`);
    }
}

function releaseScrollLock() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    document.body.classList.remove('has-home-loading-screen');

    if (reducedMotion) {
        document.body.style.removeProperty('--kronos-scrollbar-width');
        return;
    }

    const currentWidth = getComputedStyle(document.body)
        .getPropertyValue('--kronos-scrollbar-width')
        .trim();

    if (!currentWidth || currentWidth === '0px') {
        document.body.style.removeProperty('--kronos-scrollbar-width');
        return;
    }

    document.body.classList.add('is-scroll-lock-releasing');
    void document.body.offsetHeight;

    window.requestAnimationFrame(() => {
        document.body.style.setProperty('--kronos-scrollbar-width', '0px');
    });

    const finishRelease = () => {
        document.body.classList.remove('is-scroll-lock-releasing');
        document.body.style.removeProperty('--kronos-scrollbar-width');
    };

    const onTransitionEnd = (event) => {
        if (event.target !== document.body || event.propertyName !== 'padding-right') {
            return;
        }

        document.body.removeEventListener('transitionend', onTransitionEnd);
        finishRelease();
    };

    document.body.addEventListener('transitionend', onTransitionEnd);

    window.setTimeout(() => {
        if (!document.body.classList.contains('is-scroll-lock-releasing')) {
            return;
        }

        document.body.removeEventListener('transitionend', onTransitionEnd);
        finishRelease();
    }, SCROLL_RELEASE_DURATION_MS + 50);
}

function initHomeLoadingScreen() {
    const loader = document.querySelector('.kronos-home-loader');

    if (!loader) {
        return;
    }

    applyScrollLockCompensation();

    const applyWindowCenterShift = () => {
        const visual = loader.querySelector('.kronos-home-loader__visual');

        if (!visual) {
            return;
        }

        const rect = visual.getBoundingClientRect();
        const viewportCenterX = window.innerWidth * 0.5;
        const visualCenterX = rect.left + rect.width * 0.5;
        const deltaPx = viewportCenterX - visualCenterX;

        loader.style.setProperty('--kronos-home-loader-logo-shift', `${deltaPx}px`);
    };

    const startAnimation = () => {
        const animationDelay = Number.parseInt(loader.dataset.animationDelay || '120', 10);
        const dismissDelay = Number.parseInt(loader.dataset.dismissDelay || '3200', 10);
        const pauseBeforeDismiss = loader.dataset.pauseBeforeDismiss === '1';
        const openDuration = 1500;
        const centerDuration = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 0
            : 650;
        const expandDuration = 1500;
        const openEnd = animationDelay + openDuration;
        const expandDelay = openEnd + centerDuration;
        const expandedAt = expandDelay + expandDuration;
        const dismissAt = Math.max(dismissDelay, expandDelay + expandDuration);

        window.setTimeout(() => {
            loader.classList.add('is-opening');
        }, animationDelay);

        window.setTimeout(() => {
            if (centerDuration > 0) {
                applyWindowCenterShift();
                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => {
                        loader.classList.add('is-centering');
                    });
                });
            }
        }, openEnd);

        window.setTimeout(() => {
            loader.classList.add('is-expanding');
        }, expandDelay);

        window.setTimeout(() => {
            loader.classList.add('is-expanded');
        }, expandedAt);

        if (pauseBeforeDismiss) {
            window.setTimeout(() => {
                loader.classList.add('is-paused-before-dismiss');
            }, dismissAt);
            return;
        }

        window.setTimeout(() => {
            loader.classList.add('is-dismissing');
        }, dismissAt);

        window.setTimeout(() => {
            markHomeLoaderSeen(loader);
            releaseScrollLock();
            loader.remove();
        }, dismissAt + 900);
    };

    if (document.readyState === 'complete') {
        startAnimation();
        return;
    }

    window.addEventListener('load', startAnimation, { once: true });
}

document.addEventListener('DOMContentLoaded', initHomeLoadingScreen);
